<script lang="ts" generic="TRowData, TCell = any">
/**
 * This is a wrapper for the ag-grid cell editor. It is used to wrap the cell editor component and handle the special keys for the editor.
 *
 * NOTE! This one has to have an explicit setup method because there is somehting in the compiler that dissapears when we use the shorthand, the getValue method isnt compiled properly so the grid fails on edit
 * NOTE!! This is also the reason we have removed (set to any) the generics from the component.
 */
import { nextTick, ref, onMounted, defineComponent } from 'vue';
import type { ICellEditorParams } from 'ag-grid-community';
import type { ICellEditorComponentParams } from '../Classes/params';
import { useAgGridCellValueComputedWrapper } from '../Classes/AgGridCellValueComputedWrapper';
import { isCommonKey } from '@/utilities/keyboardutils';
import type { GridColumnOptions } from '../Classes';
import { type IEditorComponent } from '@/objects/gui';

type Props = {
    params: ICellEditorParams & ICellEditorComponentParams<any, any>;
};

export default defineComponent({
    setup(props: Props) {
        const component = ref<IEditorComponent & { vModel: any }>();

        const val = useAgGridCellValueComputedWrapper(props.params);

        const getValue = () => {
            return val.value;
        };

        /**
         * The keyboardevent that is triggered when the editor was started
         * @param ev
         */
        const cellEditingStarted = (ev: Event, ourColdef: GridColumnOptions<any>) => {
            if (ev instanceof KeyboardEvent && !isCommonKey(ev)) {
                if (!component.value) return;

                if (component.value?.KeyboardEvent) {
                    try {
                        component.value.KeyboardEvent(ev!);
                    } catch (error) {
                        console.warn(
                            `Error in KeyboardEvent of cell editor component for input '${ev.key}'. Ignoring`,
                            error,
                            ourColdef.cellEditor,
                        );
                    }
                } else {
                    console.log(
                        `Cell editor component does not implement KeyboardEvent method for input '${ev.key}'. Ignoring`,
                        ourColdef.cellEditor,
                    );
                }
            } else if (ev instanceof MouseEvent) {
                // TODO: Do something with the mouse event, do we need?
            }
        };

        /**
         * We handle the special keys for the editor here. This is just for the editor when it is a component in the grid
         * @param ev
         */
        const enterPressed = (ev: KeyboardEvent) => {
            if (ev.key !== 'Enter') return;

            // Trigger the end of editing and save the value and display it
            props.params?.stopEditing();
        };

        const getComponentProps = () =>
            props.params.componentProps instanceof Function ? props.params.componentProps(props.params.data) : props.params.componentProps;

        onMounted(() => {
            nextTick(() => {
                if (component.value && component.value.focus) component.value?.focus();
            });
        });

        return { component, getValue, value: val, enterPressed, cellEditingStarted, getComponentProps };
    },
});
</script>

<template>
    <component
        :is="params.comp"
        v-model="value"
        class="eq-grid-cell-editor"
        v-bind="getComponentProps()"
        ref="component"
        :row="params.data"
        @keydown="enterPressed"
        v-if="value !== undefined && params.data !== undefined"
    ></component>
</template>
